import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { TrendingDown, MouseOutlined, Warning, OpenInNew } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useSiteStore } from '../../store/site.store';
import { getCtrAnomalies, getCtrAnomalySummary, CtrAnomaly } from '../../services/ctrAnomalyService';

const formatCtr = (v: number | null | undefined) =>
  v != null ? `${(v * 100).toFixed(1)}%` : '—';

const formatPos = (v: number | null | undefined) =>
  v != null ? v.toFixed(1) : '—';

const formatDateRange = (dateFrom: string | null | undefined, dateTo: string | null | undefined) => {
  if (!dateFrom || !dateTo) return null;

  const from = new Date(dateFrom);
  const to = new Date(dateTo);

  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return `${dateFrom} to ${dateTo}`;
  }

  return `${from.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} to ${to.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

const CtrGapBar: React.FC<{ gap: number; expected: number }> = ({ gap, expected }) => {
  const pct = expected > 0 ? Math.min((gap / expected) * 100, 100) : 0;
  const color = pct > 70 ? '#f44336' : pct > 40 ? '#ff9800' : '#2196f3';
  return (
    <Tooltip title={`${pct.toFixed(0)}% below expected`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ flex: 1, height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: color } }}
        />
        <Typography variant="caption" sx={{ whiteSpace: 'nowrap', color }}>{pct.toFixed(0)}%</Typography>
      </Box>
    </Tooltip>
  );
};

export const CtrAnomalyDashboard: React.FC = () => {
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id;
  const [significantOnly, setSignificantOnly] = useState(false);
  const [viewMode, setViewMode] = useState<'latest' | 'detail'>('latest');

  const { data: summary } = useQuery({
    queryKey: ['ctr-anomaly-summary', siteId],
    queryFn: () => getCtrAnomalySummary(siteId!),
    enabled: !!siteId,
  });

  const { data: anomalies, isLoading, isError } = useQuery({
    queryKey: ['ctr-anomalies', siteId, significantOnly],
    queryFn: () => getCtrAnomalies(siteId!, significantOnly, 200),
    enabled: !!siteId,
  });

  const analysisRange = anomalies?.length
    ? formatDateRange(anomalies[0].dateFrom, anomalies[0].dateTo)
    : null;

  const latestRows = useMemo(() => {
    if (!anomalies?.length) return [];

    const grouped = new Map<string, {
      page: string;
      weightedPos: number;
      weightedActualCtr: number;
      weightedExpectedCtr: number;
      impressions: number;
      potentialClicks: number;
      isSignificant: boolean;
      queries: string[];
      dateFrom: string;
      dateTo: string;
    }>();

    anomalies.forEach((row) => {
      const key = row.page;
      const impressions = row.impressions ?? 0;
      const actualCtr = row.actualCtr ?? 0;
      const expectedCtr = row.expectedCtr ?? 0;
      const avgPosition = row.avgPosition ?? 0;

      const current = grouped.get(key);
      if (!current) {
        grouped.set(key, {
          page: row.page,
          weightedPos: avgPosition * impressions,
          weightedActualCtr: actualCtr * impressions,
          weightedExpectedCtr: expectedCtr * impressions,
          impressions,
          potentialClicks: row.potentialClicks ?? 0,
          isSignificant: !!row.isSignificant,
          queries: row.query ? [row.query] : [],
          dateFrom: row.dateFrom,
          dateTo: row.dateTo,
        });
        return;
      }

      current.weightedPos += avgPosition * impressions;
      current.weightedActualCtr += actualCtr * impressions;
      current.weightedExpectedCtr += expectedCtr * impressions;
      current.impressions += impressions;
      current.potentialClicks += row.potentialClicks ?? 0;
      current.isSignificant = current.isSignificant || !!row.isSignificant;
      if (row.query) current.queries.push(row.query);
      if (row.dateFrom < current.dateFrom) current.dateFrom = row.dateFrom;
      if (row.dateTo > current.dateTo) current.dateTo = row.dateTo;
    });

    return Array.from(grouped.values())
      .map((row) => {
        const divisor = row.impressions > 0 ? row.impressions : 1;
        const uniqueQueries = Array.from(new Set(row.queries.filter(Boolean)));
        const actualCtr = row.weightedActualCtr / divisor;
        const expectedCtr = row.weightedExpectedCtr / divisor;
        return {
          ...row,
          avgPosition: row.weightedPos / divisor,
          actualCtr,
          expectedCtr,
          ctrGap: Math.max(expectedCtr - actualCtr, 0),
          topQueries: uniqueQueries.slice(0, 5),
        };
      })
      .sort((a, b) => (b.potentialClicks ?? 0) - (a.potentialClicks ?? 0));
  }, [anomalies]);

  if (!siteId) {
    return <Alert severity="info">Select a site to view CTR anomalies.</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          📉 CTR Anomaly Detection
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Pages ranking well but receiving fewer clicks than expected. Fixing the title or meta
          description can unlock traffic without improving rankings.
        </Typography>
        {analysisRange && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Analysis window: {analysisRange}
          </Typography>
        )}
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Total Anomalies</Typography>
              <Typography variant="h4" fontWeight={700}>{summary?.totalAnomalies ?? '—'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Statistically Significant</Typography>
              <Typography variant="h4" fontWeight={700} color="error.main">
                {summary?.significantAnomalies ?? '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Switch
              checked={significantOnly}
              onChange={(e) => setSignificantOnly(e.target.checked)}
              color="error"
            />
          }
          label="Show significant anomalies only (z-score ≥ 1.96)"
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, value) => {
            if (value) setViewMode(value);
          }}
        >
          <ToggleButton value="latest">Latest View</ToggleButton>
          <ToggleButton value="detail">Detail View</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error">Failed to load CTR anomaly data. Run the <strong>ctr-anomaly</strong> batch job first.</Alert>
      ) : !anomalies?.length ? (
        <Alert severity="success">No CTR anomalies detected for this site. 🎉</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                <TableCell>{viewMode === 'latest' ? 'Page / Top Queries' : 'Page / Query'}</TableCell>
                <TableCell align="center">Position</TableCell>
                <TableCell align="center">Actual CTR</TableCell>
                <TableCell align="center">Expected CTR</TableCell>
                <TableCell>CTR Gap</TableCell>
                <TableCell align="right">Impressions</TableCell>
                <TableCell align="right">Lost Clicks</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(viewMode === 'latest' ? latestRows : anomalies).map((row: CtrAnomaly | any) => (
                <TableRow key={viewMode === 'latest' ? row.page : row.id} hover sx={{ opacity: row.isSignificant ? 1 : 0.7 }}>
                  <TableCell>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {row.page}
                        </Typography>
                        <OpenInNew sx={{ fontSize: 12, color: 'text.disabled', cursor: 'pointer' }}
                          onClick={() => window.open(row.page, '_blank')} />
                      </Box>
                      {viewMode === 'latest' ? (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {row.topQueries?.length ? `Top queries (${row.topQueries.length}):` : 'Top queries:'}
                          </Typography>
                          {(row.topQueries?.length ? row.topQueries : ['No query detail']).slice(0, 5).map((query: string, idx: number) => (
                            <Typography key={`${row.page}-q-${idx}`} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {idx + 1}. {query}
                            </Typography>
                          ))}
                        </>
                      ) : (
                        row.query && (
                          <Typography variant="caption" color="text.secondary">
                            🔍 {row.query}
                          </Typography>
                        )
                      )}
                      {formatDateRange(row.dateFrom, row.dateTo) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          Window: {formatDateRange(row.dateFrom, row.dateTo)}
                        </Typography>
                      )}
                      {viewMode === 'detail' && (row.country || row.device) && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                          {row.country && <Chip label={row.country.toUpperCase()} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />}
                          {row.device && <Chip label={row.device} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>{formatPos(row.avgPosition)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      {formatCtr(row.actualCtr)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="success.main">
                      {formatCtr(row.expectedCtr)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <CtrGapBar gap={row.ctrGap} expected={row.expectedCtr} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{row.impressions?.toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="warning.main" fontWeight={600}>
                      {row.potentialClicks != null ? Math.round(row.potentialClicks).toLocaleString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {row.isSignificant ? (
                      <Chip icon={<Warning />} label="Significant" size="small" color="error" variant="outlined" />
                    ) : (
                      <Chip label="Minor" size="small" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CtrAnomalyDashboard;
